import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section } from "@/components/site/Blocks";
import { glossary } from "@/data/glossary";
import { buildHead } from "@/lib/seo-head";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";
import heroGlossary from "@/assets/hero/hero-glossary.jpg.asset.json";

const HERO = {
  src: heroGlossary.url,
  alt: "A real estate investor at a desk using a laptop to look up financing terms, with a second monitor showing dictionary-style definition pages, a printed term sheet, notebook, calculator and a model house — investor loan glossary",
};

const URL = "/glossary";
const KEYWORDS = [
  "DSCR loan glossary",
  "real estate investing terms",
  "mortgage underwriting terms",
  "DSCR definition",
  "LTV definition",
  "BRRRR strategy definition",
  "investor loan terminology",
  "business purpose loan terms",
  "rental property financing glossary",
  "real estate lending dictionary",
].join(", ");

export const Route = createFileRoute("/glossary/")({
  head: () =>
    buildHead({
      title: "Investor Loan Glossary — DSCR, LTV, BRRRR & More",
      description:
        "Plain-English definitions of the DSCR, underwriting, and investor-strategy terms that come up in real-estate financing.",
      url: URL,
      keywords: KEYWORDS,
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Glossary", url: URL },
      ],
      service: { name: "Investor Loan Education & Terminology" },
      image: HERO.src,
      imageAlt: HERO.alt,
      extraJsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "DefinedTermSet",
          name: "Investor Loan Glossary",
          hasDefinedTerm: glossary.map((t) => ({
            "@type": "DefinedTerm",
            name: t.term,
            description: t.short,
            url: `/glossary/${t.slug}`,
          })),
        },
      ],
    }),
  component: GlossaryIndex,
});

function GlossaryIndex() {
  const grouped = glossary.reduce<Record<string, typeof glossary>>((acc, t) => {
    (acc[t.category] ||= []).push(t);
    return acc;
  }, {});
  return (
    <SiteShell>
      <PageHero eyebrow="Glossary" title="Investor loan glossary" subtitle="Plain-English definitions for the terms that come up on every deal." image={HERO} />
      <PageEssentials />
      <Section>
        <div className="space-y-10">
          {Object.entries(grouped).map(([cat, terms]) => (
            <div key={cat}>
              <h2 className="font-display text-xl font-bold text-brand-dark">{cat}</h2>
              <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {terms.map((t) => (
                  <Link key={t.slug} to="/glossary/$term" params={{ term: t.slug }} className="rounded-lg border border-border bg-card p-4 transition-colors hover:border-brand-gold">
                    <div className="font-semibold text-brand-dark">{t.term}</div>
                    <p className="mt-1 text-sm text-muted-foreground">{t.short}</p>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>
    <PageOutro />
      </SiteShell>
  );
}
